import { fetch } from '@atlassian/eoc-fetch';

import type {
	AlertPolicy,
	AlertPolicyResponse,
	ChangeAlertPolicyOrderParamsWithTeamId,
	ChangeAlertPolicyStateParamsWithTeamId,
	CreatePolicyParamsWithTeamId,
	GlobalAlertPolicyResponse,
	NotificationPolicy,
	NotificationPolicyResponse,
	PolicyMaintenanceRights,
	UpdatePolicyParams,
	UpdateUIPreferenceParams,
} from '../../common/types';

export const getTeamPolicies = (teamId: string): Promise<AlertPolicyResponse> => {
	return fetch<AlertPolicyResponse>(`/v1/teams/${teamId}/policies?type=alert`);
};

export const getGlobalPolicies = (teamId?: string | null): Promise<GlobalAlertPolicyResponse> => {
	if (teamId) {
		return fetch<GlobalAlertPolicyResponse>(`/v1/alerts/policies?preferredUITeamId=${teamId}`);
	}

	return fetch<GlobalAlertPolicyResponse>(`/v1/alerts/policies`);
};

export const getNotificationPolicies = (teamId: string): Promise<NotificationPolicyResponse> => {
	return fetch<NotificationPolicyResponse>(`/v1/teams/${teamId}/policies?type=notification`);
};

export const getPolicyMaintenanceRights = (): Promise<PolicyMaintenanceRights> => {
	return fetch<PolicyMaintenanceRights>(`/v1/alerts/ui-preferences/policy-maintenance-rights`);
};

export const updateUIPreferenceEndpoint = ({
	teamId,
	...payload
}: UpdateUIPreferenceParams): Promise<Response> => {
	let url = `/v1/alerts/ui-preferences/policies`;

	if (teamId) {
		url = `${url}?teamId=${teamId}`;
	}

	return fetch(url, {
		method: 'PATCH',
		body: payload,
		responseType: 'headers',
	});
};

export const changePolicyOrderEndpoint = (
	payload: ChangeAlertPolicyOrderParamsWithTeamId,
): Promise<Response> => {
	return fetch(`/v1/teams/${payload.teamId}/policies/${payload.id}/order`, {
		method: 'PATCH',
		body: { after: payload.after },
		responseType: 'headers',
	});
};

export const enablePolicyEndpoint = (
	payload: ChangeAlertPolicyStateParamsWithTeamId,
): Promise<Response> => {
	return fetch(`/v1/teams/${payload.teamId}/policies/${payload.id}/enable`, {
		method: 'POST',
		responseType: 'headers',
	});
};

export const disablePolicyEndpoint = (
	payload: ChangeAlertPolicyStateParamsWithTeamId,
): Promise<Response> => {
	return fetch(`/v1/teams/${payload.teamId}/policies/${payload.id}/disable`, {
		method: 'POST',
		responseType: 'headers',
	});
};

export const deletePolicyEndpoint = (
	payload: ChangeAlertPolicyStateParamsWithTeamId,
): Promise<Response> => {
	return fetch(`/v1/teams/${payload.teamId}/policies/${payload.id}`, {
		method: 'DELETE',
		responseType: 'headers',
	});
};

export const createPolicyEndpoint = (
	payload: CreatePolicyParamsWithTeamId,
): Promise<AlertPolicy | NotificationPolicy> => {
	return fetch<AlertPolicy | NotificationPolicy>(`/v1/teams/${payload.teamId}/policies`, {
		method: 'POST',
		body: payload,
	});
};

export const updatePolicyEndpoint = (
	payload: UpdatePolicyParams,
): Promise<AlertPolicy | NotificationPolicy> => {
	return fetch<AlertPolicy | NotificationPolicy>(
		`/v1/teams/${payload.teamId}/policies/${payload.id}`,
		{
			method: 'PUT',
			body: payload,
		},
	);
};
